import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Upload, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; };

  const close = () => { reset(); onClose?.(); };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a CSV file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/compliances/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} record(s)${data.skipped_duplicates ? ` · Skipped ${data.skipped_duplicates} duplicate(s)` : ""}`);
        onDone?.();
      } else if (data.skipped_duplicates > 0 && !data.errors?.length) {
        toast.info(`All rows were duplicates — nothing imported.`);
      } else {
        toast.error("No rows were imported. See errors below.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">Import Compliances from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV with columns matching the template. Partner and responsible names are free text (optional).
            Month may be number or name (e.g. <span className="font-mono-num">6</span> or <span className="font-mono-num">June</span>).
            Duplicate records (same client + type + period) are skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
          <div className="font-medium text-slate-800 mb-1">Columns (compliance_type, client_name, deadline required)</div>
          <div className="font-mono-num text-[11px] break-all">
            compliance_type, client_name, partner_name, responsible_person_name, fees, period_kind, month, quarter, year, period_start, period_end, deadline, priority, status, invoice_status, notes
          </div>
          <div className="mt-2">
            <a
              href={`${API_BASE}/compliances/import/template.csv`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-blue-700 hover:underline text-xs font-medium"
              data-testid="import-template-link"
            >
              <Download className="w-3.5 h-3.5" /> Download CSV template
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="import-form">
          <label
            className="flex items-center justify-center gap-3 w-full rounded-lg border-2 border-dashed border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50 transition-colors py-8 cursor-pointer"
            data-testid="import-drop"
          >
            <Upload className="w-5 h-5 text-slate-500" />
            <div className="text-sm">
              {file ? (
                <span className="font-medium text-slate-800 font-mono-num">{file.name}</span>
              ) : (
                <>
                  <span className="text-slate-700 font-medium">Choose a CSV file</span>
                  <span className="text-slate-500"> or drag it here</span>
                </>
              )}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="import-file-input"
            />
          </label>

          {result && (
            <div className="rounded-md border border-slate-200 bg-white p-3 text-sm space-y-1.5" data-testid="import-result">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-4 h-4" />
                <span>Created <span className="font-mono-num font-medium">{result.created}</span> record(s)</span>
              </div>
              {result.skipped_duplicates > 0 && (
                <div className="text-slate-600">Skipped <span className="font-mono-num font-medium">{result.skipped_duplicates}</span> duplicate(s)</div>
              )}
              {result.errors?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 text-red-700 mt-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>Errors on {result.errors.length} row(s):</span>
                  </div>
                  <ul className="max-h-40 overflow-auto mt-1 text-xs font-mono-num text-red-700 pl-6 list-disc">
                    {result.errors.map((er) => (
                      <li key={`err-${er.row}-${er.error}`}>Row {er.row}: {er.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} data-testid="import-close">Close</Button>
            <Button type="submit" disabled={!file || uploading} data-testid="import-submit">
              {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Import
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
